/**
 * tractModel.ts — CensusTract type + SCORE-06 customer-facing labels
 *
 * Single source of truth for display labels (Noreen's naming, SCORE-06).
 * Mirrors the field contract from the API's parseTractRow transform.
 *
 * Plan 02.1-02: Additive expansion — new fields are optional (number | null)
 * for backward-compatibility with existing test fixtures and callers that
 * construct CensusTract with only the original 8 fields.
 */
import { humanizeEnum } from './labels'

// Properties returned in bbox GeoJSON feature.properties (raw PostGIS column names)
export interface TractFeatureProps {
  tract_geoid: string
  lvi: number
  cdpi: number
  cmpi: number
  ppi_v11: number
  gap_score: number
  dominant_type: string
  lvi_pctile_state: number | null
  // Plan 02.1-02: new fields (all optional — bbox response may not include all)
  CS?: number | null
  RDS?: number | null
  HCC?: number | null
  HCO?: number | null
  DTS?: number | null
  ppi_pctile_state?: number | null
  cdpi_pctile_state?: number | null
  cmpi_pctile_state?: number | null
  gap_pctile_state?: number | null
  population?: number | null
  population_density?: number | null
  median_hh_income?: number | null
  ev_reg_per_1000?: number | null
  pct_renter?: number | null
  pct_college_educated?: number | null
  mean_commute_time?: number | null
  vacancy_rate?: number | null
  pct_no_vehicle?: number | null
  mean_vehicles_per_hh?: number | null
  pct_drove_alone?: number | null
  median_home_value?: number | null
  sf_detached_share?: number | null
  pct_owner_occupied?: number | null
  multifamily_share?: number | null
  pop_growth_5yr?: number | null
  daytime_pop_ratio?: number | null
  jobs_retail_pct?: number | null
  jobs_food_accommodation_pct?: number | null
  jobs_entertainment_pct?: number | null
  jobs_healthcare_pct?: number | null
  jobs_per_sq_mile?: number | null
  station_count?: number | null
  ports_total?: number | null
  dcfc_ports?: number | null
  stations_per_1k_ev?: number | null
  corridor_exposure_score?: number | null
  nearest_dcfc_mi?: number | null
  avg_dist_interstate_mi?: number | null
  pct_ports_near_ramp?: number | null
  pct_ports_at_rest_area?: number | null
  pct_ports_on_afc?: number | null
  household_density?: number | null
}

// Typed active-tract record (what the score panel displays)
export interface CensusTract {
  tractId: string
  lviScore: number // Opportunity Score (LVI)
  cdpiScore: number // Demand Potential Index
  cmpiScore: number // Market Pressure Index
  ppiScore: number // Pricing Power Index
  gapScore: number // Gap Score
  dominantType: string
  lviPctileState: number | null
  // Plan 02.1-02: component scores
  CS?: number | null
  RDS?: number | null
  HCC?: number | null
  HCO?: number | null
  DTS?: number | null
  // Plan 02.1-02: state-percentile fields
  ppiPctileState?: number | null
  cdpiPctileState?: number | null
  cmpiPctileState?: number | null
  gapPctileState?: number | null
  // Plan 02.1-02: demographic + supply fields
  population?: number | null
  populationDensity?: number | null
  medianHhIncome?: number | null
  evRegPer1000?: number | null
  pctRenter?: number | null
  pctCollegeEducated?: number | null
  meanCommuteTime?: number | null
  vacancyRate?: number | null
  pctNoVehicle?: number | null
  meanVehiclesPerHh?: number | null
  pctDroveAlone?: number | null
  medianHomeValue?: number | null
  sfDetachedShare?: number | null
  pctOwnerOccupied?: number | null
  multifamilyShare?: number | null
  popGrowth5yr?: number | null
  daytimePopRatio?: number | null
  jobsRetailPct?: number | null
  jobsFoodAccommodationPct?: number | null
  jobsEntertainmentPct?: number | null
  jobsHealthcarePct?: number | null
  jobsPerSqMile?: number | null
  stationCount?: number | null
  portsTotal?: number | null
  dcfcPorts?: number | null
  stationsPer1kEv?: number | null
  corridorExposureScore?: number | null
  nearestDcfcMi?: number | null
  avgDistInterstateMi?: number | null
  pctPortsNearRamp?: number | null
  pctPortsAtRestArea?: number | null
  pctPortsOnAfc?: number | null
  householdDensity?: number | null
}

// SCORE-06: Noreen-approved customer-facing labels
export const SCORE_LABELS = {
  lvi: 'Opportunity Score',
  cdpi: 'Demand Potential Index',
  cmpi: 'Market Pressure Index',
  ppi_v11: 'Pricing Power Index',
  gap_score: 'Gap Score',
} as const

export type ScoreLabelKey = keyof typeof SCORE_LABELS
export type ScoreLabel = (typeof SCORE_LABELS)[ScoreLabelKey]

/**
 * T-616 — the customer name for an area we hold no residential measurement for.
 *
 * Noreen answered the T-604 rules question verbatim: "I looked and I call those areas just
 * 'Non-Residential'." Two different geographies arrive at that state by two different routes:
 *
 *   tracts        carry a STORED `Non-Residential` dominant_type (the producer's exclusion route)
 *   block groups  carry NULL, and always will — T-602 made that null honest rather than
 *                 fabricating 0.0/0.1 scores, so there is no token to store and none is wanted
 *
 * The block-group side is therefore a DISPLAY mapping, not a stored label: the database keeps NULL
 * (storing a string would assert a classification the pipeline never measured, and force an enum
 * change plus a reload). What must NOT differ is the word the reader sees, so both routes resolve
 * to this one constant — including `DOMINANT_TYPE_LABELS` below, which cannot now drift from it.
 */
export const NON_RESIDENTIAL_LABEL = 'Non-Residential'

/**
 * True only for the state that earns the name: a block group covers the point and carries no
 * residential input at all (stored label NULL, both scores NULL — the API reports it as
 * `unscored_bg`).
 *
 * Deliberately false for its two neighbours, which are different facts:
 *   `unclassified_bg`   a REAL measurement that met neither residential threshold — naming it
 *                       Non-Residential would report a finding we never made
 *   `no_bg_coverage`    no block group here at all — not knowing an area is not the same as
 *                       knowing it is not residential
 *
 * The predicate lives beside the word on purpose. A second surface can copy a string; it cannot
 * as easily copy a boundary, and the boundary is the part that would go quietly wrong.
 */
export function isNonResidentialBasis(basis: string | null | undefined): boolean {
  return basis === 'unscored_bg'
}

// SCORE-06: customer-facing labels for each canonical dominant_type. Single source
// of truth — the tract filter, score panel, and drill-down all read from here so a
// raw enum token (e.g. "Suburban_SFH") is never shown to a user.
export const DOMINANT_TYPE_LABELS: Record<string, string> = {
  Corridor: 'Corridor',
  Retail: 'Retail',
  Residential_MFH: 'Residential (Multi-family)',
  Suburban_SFH: 'Suburban (Single-family)',
  // T-616: bound to the constant, so the tract label and the block-group display name are the
  // same string by construction rather than by two authors agreeing.
  'Non-Residential': NON_RESIDENTIAL_LABEL,
}

/** Friendly label for a tract dominant_type; humanizes unmapped values as a fallback.
 *  Null-safe (F2/T-082): bbox feature props may omit dominant_type, so a nullish
 *  value renders an em-dash placeholder instead of crashing the drill-down. */
export function dominantTypeLabel(type: string | null | undefined): string {
  if (!type) return '—'
  return DOMINANT_TYPE_LABELS[type] ?? humanizeEnum(type)
}

// Phase 2 (Plan 02-04): web-side mirror of apps/api/src/lib/siteScore.ts
// (monorepo boundary — no cross-package import; keep in sync manually)
// Also mirrors apps/api/src/lib/labelMap.ts SITE_FACTOR_LABELS.

/** Per-factor contribution labels — mirrors API SITE_FACTOR_LABELS. */
export const SITE_FACTOR_LABELS = {
  corridorProximity: 'Corridor Proximity',
  competitionPenalty: 'EV Competition',
  destinationBonus: 'Destination Density',
} as const

export type SiteFactorKey = keyof typeof SITE_FACTOR_LABELS

/**
 * Why the R (residential context) term came out the way it did — mirrors the API's
 * `v2.rBasis` union (apps/api/src/lib/bgContext.ts). Five of these six mean R = 0 for
 * materially different reasons, which is the entire point of reporting a basis at all:
 * `no_bg_coverage` and `unscored_bg` are claims about the data (no block group here vs. a block
 * group we have no scores for), `disabled` is a decision, `unavailable` is a failure, and
 * `unclassified_bg` is a genuine measurement. They must never collapse.
 *
 * T-586 added `unscored_bg`. Before T-583 gave the producer a plurality fallback, an unlabelled
 * block group meant "scored, met neither threshold"; now the producer labels everything it has a
 * score for, so an unlabelled row means missing data instead — a different fact needing its own
 * member rather than a reworded sentence over the old one.
 */
export type RBasis =
  'classified' | 'unclassified_bg' | 'unscored_bg' | 'no_bg_coverage' | 'disabled' | 'unavailable'

/**
 * Authoritative site-score v2 breakdown — mirrors the `v2` block emitted by
 * POST /api/score/site (apps/api/src/lib/siteScore.ts `SiteScoreV2Result`).
 *
 * `degraded` is the truth-in-labeling signal (T-352): when a tract baseline the
 * recompute needs is unavailable the composite site LVI is NOT recomputed — the stored
 * tract LVI is returned instead, with `degraded: true`. The UI must then show the
 * score as the tract-level baseline, not imply a live site-specific LVI. `degraded`
 * is false only when every recompute input was present. `dAvailable` flags
 * whether the D (ramp/rest-area) source resolved for this call.
 *
 * T-551 widened the sub-scores to nullable. Each is null exactly when the tract
 * component it consumes was unavailable, so a NUMBER here always means measured data —
 * never a stand-in for a missing input. Render the absence; do not coerce it to 0,
 * which is what the API used to do and is the bug that made this field nullable.
 * (`tractLvi`/`siteLvi` stay non-null: a tract with no LVI is rejected as unscoreable
 * by the route rather than returned as a degrade, so a response always carries one.)
 */
export interface SiteScoreV2 {
  degraded: boolean
  degradedReason: string | null
  dAvailable: boolean
  tractLvi: number
  siteLvi: number
  siteCdpi: number | null
  siteCmpi: number | null
  sitePpi: number | null
  siteSam: number | null
  gapScore: number | null
  weights: { gap: number; ppi: number; sam: number }
  adjustments: { d: number | null; f: number; m: number; r: number }
  /** Why R is what it is (T-414). Four of the five bases all mean R = 0 for different
   *  reasons, so the UI must never render the number without the reason. Optional because a
   *  cached response from before T-414 has no basis to report — and a MISSING basis is itself
   *  a distinct state from any of the five, so it renders nothing rather than guessing one. */
  rBasis?: RBasis
  /** The block group consulted, when one was found. */
  bgGeoid?: string | null
  /** Distance to nearest highway ramp or rest area (miles). null when unavailable. */
  rampDistanceMi?: number | null
  /** Competition details for the M adjustment. */
  competition?: {
    weighted: number
    distinctNetworks: number
    radiusMi: number
  }
  /** POI count used for the F adjustment. */
  poiCount?: number
}

/** Site-level composite score result — mirrors apps/api/src/lib/siteScore.ts SiteScoreResult.
 *  Key-for-key match; update both files when the API shape changes (D-04 swap seam). */
/** T-451: the subset of a server NormalizedPOI the PDF nearby block reads off the site payload. */
export interface NearbyPoiPayload {
  id: string
  category: string
  name: string
  lat: number
  lng: number
  /** T-654: metres from the searched site (ALS Distance, computed server-side). The canonical
   *  0.25 mi amenity figure both surfaces render re-buckets these POIs by this distance, so reading
   *  one server-computed value on both is a stronger guarantee than each re-deriving haversine.
   *  Optional: an older cached response, or a POI whose provider omitted a distance, has none. */
  dist?: number
}

export interface SiteScoreResult {
  /** The composite, customer-facing score (0–100). */
  opportunityScore: number
  /** The tract LVI baseline (unchanged from PostGIS). */
  lviBaseline: number
  /** The site-specific delta: Σ(weightᵢ · factorᵢ). Zero with neutral weights. */
  siteAdjustment: number
  /** Per-factor contributions — powers the drill-down panel (SCORE-03). */
  factorContributions: {
    corridorProximity: number
    competitionPenalty: number
    destinationBonus: number
  }
  // Plan 02.1-02: optional fields produced by /api/score/site (Plan 04); older callers compile without them
  amenities?: {
    categories: Record<string, number>
    /** T-497: the radius (miles) the `categories` counts were gathered at — the F-term POI radius
     *  (API tiers.radii.poiMi, default 0.25). Lets the "Nearby amenities" footnote state the actual
     *  query radius. Optional: an older cached response omits it (panel falls back to the default). */
    poiRadiusMi?: number
    /** T-451: the full nearby-POI set (all 12 categories) for the PDF one-pager's nearby block,
     *  from the supplemental all-category gather. Empty when that gather found nothing or failed. */
    nearbyPois?: NearbyPoiPayload[]
    /** RAW items the supplemental gather received, before the 12-category filter — lets the PDF
     *  tell a capped fetch from a complete one. */
    poiFetched?: number
    /** The result ceiling that gather ran under (ALS: 100). */
    poiResultCap?: number
    /** T-644: RAW items the FILTERED scoring/drill-down gather received (the one that produces
     *  `categories`), and the ceiling it ran under (ALS: 50). Distinct from poiFetched/poiResultCap
     *  above, which describe the supplemental all-category gather. When poiCountFetched >=
     *  poiCountCap the drill-down counts are truncation floors, not exact totals. Optional: an older
     *  cached response omits them (the panel then shows bare counts, as before). */
    poiCountFetched?: number
    poiCountCap?: number
  }
  evCompetition?: { stationCount: number }
  /** T-352: authoritative v2 breakdown incl. the `degraded` truth-in-labeling flag.
   *  Optional so pre-v2 callers/fixtures still compile. */
  v2?: SiteScoreV2
}

/** Drive-time isochrone result from POST /api/isochrone (T-135). `geojson` is a single
 *  GeoJSON Feature (Polygon for one reachable area, MultiPolygon for disconnected areas),
 *  or null when the service returns no reachable-area data for the point. */
export interface IsochroneResult {
  geojson: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null
  minutes: number
  origin: { lat: number; lng: number }
}

/** Map raw bbox feature properties to the CensusTract display type. */
export function featurePropsToTract(props: TractFeatureProps): CensusTract {
  return {
    // Original 8 fields (unchanged)
    tractId: props.tract_geoid,
    lviScore: props.lvi,
    cdpiScore: props.cdpi,
    cmpiScore: props.cmpi,
    ppiScore: props.ppi_v11,
    gapScore: props.gap_score,
    dominantType: props.dominant_type,
    lviPctileState: props.lvi_pctile_state,
    // Plan 02.1-02: component scores
    CS: props.CS ?? null,
    RDS: props.RDS ?? null,
    HCC: props.HCC ?? null,
    HCO: props.HCO ?? null,
    DTS: props.DTS ?? null,
    // State-percentile fields
    ppiPctileState: props.ppi_pctile_state ?? null,
    cdpiPctileState: props.cdpi_pctile_state ?? null,
    cmpiPctileState: props.cmpi_pctile_state ?? null,
    gapPctileState: props.gap_pctile_state ?? null,
    // Demographic + supply fields
    population: props.population ?? null,
    populationDensity: props.population_density ?? null,
    medianHhIncome: props.median_hh_income ?? null,
    evRegPer1000: props.ev_reg_per_1000 ?? null,
    pctRenter: props.pct_renter ?? null,
    pctCollegeEducated: props.pct_college_educated ?? null,
    meanCommuteTime: props.mean_commute_time ?? null,
    vacancyRate: props.vacancy_rate ?? null,
    pctNoVehicle: props.pct_no_vehicle ?? null,
    meanVehiclesPerHh: props.mean_vehicles_per_hh ?? null,
    pctDroveAlone: props.pct_drove_alone ?? null,
    medianHomeValue: props.median_home_value ?? null,
    sfDetachedShare: props.sf_detached_share ?? null,
    pctOwnerOccupied: props.pct_owner_occupied ?? null,
    multifamilyShare: props.multifamily_share ?? null,
    popGrowth5yr: props.pop_growth_5yr ?? null,
    daytimePopRatio: props.daytime_pop_ratio ?? null,
    jobsRetailPct: props.jobs_retail_pct ?? null,
    jobsFoodAccommodationPct: props.jobs_food_accommodation_pct ?? null,
    jobsEntertainmentPct: props.jobs_entertainment_pct ?? null,
    jobsHealthcarePct: props.jobs_healthcare_pct ?? null,
    jobsPerSqMile: props.jobs_per_sq_mile ?? null,
    stationCount: props.station_count ?? null,
    portsTotal: props.ports_total ?? null,
    dcfcPorts: props.dcfc_ports ?? null,
    stationsPer1kEv: props.stations_per_1k_ev ?? null,
    corridorExposureScore: props.corridor_exposure_score ?? null,
    nearestDcfcMi: props.nearest_dcfc_mi ?? null,
    avgDistInterstateMi: props.avg_dist_interstate_mi ?? null,
    pctPortsNearRamp: props.pct_ports_near_ramp ?? null,
    pctPortsAtRestArea: props.pct_ports_at_rest_area ?? null,
    pctPortsOnAfc: props.pct_ports_on_afc ?? null,
    householdDensity: props.household_density ?? null,
  }
}
